
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Eye, EyeOff, User, Mail, KeyRound } from 'lucide-react';

const UserSettings = () => {
  const { user, updateUserMetadata, updateUserEmail, updateUserPassword } = useAuth();
  const { toast } = useToast();

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState({ info: false, email: false, password: false });

  useEffect(() => {
    if (user) {
      setFullName(user.user_metadata?.full_name || '');
      setPhone(user.user_metadata?.phone || '');
      setEmail(user.email || '');
    }
  }, [user]);

  const handleInfoSubmit = async (e) => {
    e.preventDefault();
    setLoading(prev => ({ ...prev, info: true }));
    try {
      await updateUserMetadata({ full_name: fullName, phone });
      toast({ title: 'Sucesso!', description: 'Suas informações foram atualizadas.' });
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(prev => ({ ...prev, info: false }));
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (email === user.email) {
      toast({ title: 'Aviso', description: 'O novo e-mail é igual ao atual.', variant: 'default' });
      return;
    }
    setLoading(prev => ({ ...prev, email: true }));
    try {
      await updateUserEmail(email);
      toast({ title: 'Verifique seu E-mail', description: 'Um link de confirmação foi enviado para o seu novo endereço de e-mail.' });
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(prev => ({ ...prev, email: false }));
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast({ title: 'Erro', description: 'As senhas não coincidem.', variant: 'destructive' });
      return;
    }
    if (password.length < 6) {
      toast({ title: 'Erro', description: 'A senha deve ter no mínimo 6 caracteres.', variant: 'destructive' });
      return;
    }
    setLoading(prev => ({ ...prev, password: true }));
    try {
      await updateUserPassword(password);
      toast({ title: 'Sucesso!', description: 'Sua senha foi alterada.' });
      setPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(prev => ({ ...prev, password: false }));
    }
  };
  
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
      },
    }),
  };

  return (
    <div className="space-y-6">
      <div>
        <Card className="glass-effect">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><User className="w-5 h-5" /> Informações Pessoais</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleInfoSubmit} className="space-y-4">
              <div>
                <Label htmlFor="fullName">Nome Completo</Label>
                <Input id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="phone">Número de Celular (com DDD)</Label>
                <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              <Button type="submit" disabled={loading.info}>{loading.info ? 'Salvando...' : 'Salvar Informações'}</Button>
            </form>
          </CardContent>
        </Card>
      </div>
      
      <div>
        <Card className="glass-effect">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5" /> Alterar E-mail</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Novo E-mail</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                 <p className="text-xs text-white/70 mt-2">Seu e-mail atual é: {user?.email}. Um link de confirmação será enviado para o novo e-mail.</p>
              </div>
              <Button type="submit" disabled={loading.email}>{loading.email ? 'Atualizando...' : 'Atualizar E-mail'}</Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <div>
        <Card className="glass-effect">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5" /> Alterar Senha</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="relative">
                <Label htmlFor="password">Nova Senha</Label>
                <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Mínimo 6 caracteres" value={password} onChange={(e) => setPassword(e.target.value)} />
                <Button variant="ghost" size="icon" type="button" className="absolute right-1 bottom-1 h-7 w-7" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <div className="relative">
                <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
                <Input id="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} placeholder="Repita a nova senha" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                <Button variant="ghost" size="icon" type="button" className="absolute right-1 bottom-1 h-7 w-7" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <Button type="submit" disabled={loading.password}>{loading.password ? 'Atualizando...' : 'Atualizar Senha'}</Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserSettings;

